// @ts-nocheck

import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  basketDelItem,
  basketItemBankerChange,
  basketItemStakeChange,
  basketItemSelectedFreebetChange,
  basketOutcomeUnselected,
} from '../../actions/basket';
import { BasketCardContext } from '../../contexts/BasketCardContext';
import { useShallowEqualSelector } from '../../hooks';
import useMergeProps from '../../hooks/useMergeProps';
import useRouter from '../../hooks/useRouter';

import BasketCard from './BasketCard';
import makeBasketCardPropsSelector from './BasketCard.selector';

type PropsType = {
  itemId: string | number;
  isValidating: boolean;
};

const BasketCardContainer = (ownProps: PropsType) => {
  const dispatch = useDispatch();
  const { history, location, params } = useRouter();
  const { itemId } = ownProps;

  const selector = makeBasketCardPropsSelector();
  const selectorProps = useMemo(() => ({ history, location, params, itemId }), [history, location, params, itemId]);
  const selectedProps = useShallowEqualSelector((state) => selector(state, selectorProps));

  const { oddId, banker, matchId, readOnly, cashout, eligibleFreebets = {}, selectedFreebetId } = selectedProps;

  const bindedActions = useMemo(
    () =>
      bindActionCreators(
        {
          basketDelItem,
          basketItemBankerChange,
          basketItemStakeChange,
          basketItemSelectedFreebetChange,
          basketOutcomeUnselected,
        },
        dispatch,
      ),
    [dispatch],
  );

  const canDelItem = !readOnly && !cashout;

  const actionsProps = useMemo(
    () => ({
      handleDelItem: canDelItem
        ? () => {
            bindedActions.basketOutcomeUnselected({ oddId, matchId });
          }
        : null,
      handleSelectBanker: () => {
        bindedActions.basketItemBankerChange({ oddId, banker: !banker });
      },
      handleCheckEligibleFreebets: (amount, eligibleFreebetId) => () => {
        const freebetId = eligibleFreebets[eligibleFreebetId].freebetId;
        const formattedAmount = parseFloat(amount / 100).toString();

        // dispatch an action to update the `selectedFreebetId` state
        bindedActions.basketItemSelectedFreebetChange({
          oddId,
          freebetId: selectedFreebetId === parseInt(eligibleFreebetId, 10) ? null : freebetId,
        });

        // dispatch an action to update the `stake` state
        bindedActions.basketItemStakeChange({ oddId, stake: formattedAmount });
      },
    }),
    [banker, bindedActions, canDelItem, matchId, oddId, eligibleFreebets, selectedFreebetId],
  );

  const forwardedState = useMemo(() => ({ itemId }), [itemId]);

  const enhancedProps = useMergeProps({ selectedProps, actionsProps, ownProps });

  return (
    <BasketCardContext.Provider value={forwardedState}>
      <BasketCard {...enhancedProps} />
    </BasketCardContext.Provider>
  );
};

export default React.memo<PropsType>(BasketCardContainer);
